import { Link } from "react-router-dom";
import PageHero from "../components/site/PageHero";
import CTABanner from "../components/site/CTABanner";
import useSEO from "../hooks/useSEO";
import {
  BellRing,
  Lightbulb,
  Flame,
  ClipboardCheck,
  Wrench,
  Cog,
  ArrowRight,
  Building2,
} from "lucide-react";

const GROUPS = [
  {
    title: "New Installations",
    icon: BellRing,
    items: [
      "Fire alarm design and installation",
      "Emergency lighting installation",
      "Fire extinguisher supply and installation",
      "System commissioning",
      "Extensions to existing systems",
    ],
    to: "/services/fire-alarms",
  },
  {
    title: "Testing & Servicing",
    icon: ClipboardCheck,
    items: [
      "Fire alarm testing",
      "Emergency lighting testing",
      "Fire extinguisher servicing",
      "Functional inspections",
      "Service records and documentation",
    ],
    to: "/services/testing-certification",
  },
  {
    title: "Maintenance & Repairs",
    icon: Wrench,
    items: [
      "Planned preventative maintenance",
      "Fault diagnosis",
      "Reactive repairs",
      "Component and battery replacement",
      "System upgrades and obsolete equipment replacement",
    ],
    to: "/services/repairs-upgrades",
  },
  {
    title: "Multi-Site Support",
    icon: Building2,
    items: [
      "Coordinated servicing schedules",
      "Central documentation",
      "Site-by-site reporting",
      "Maintenance planning",
      "Consistent process across sites",
    ],
    to: "/services/maintenance",
  },
];

const CARDS = [
  { slug: "fire-alarms", icon: BellRing, title: "Fire Alarm Systems", to: "/services/fire-alarms" },
  { slug: "emergency-lighting", icon: Lightbulb, title: "Emergency Lighting", to: "/services/emergency-lighting" },
  { slug: "fire-extinguishers", icon: Flame, title: "Fire Extinguishers", to: "/services/fire-extinguishers" },
  { slug: "testing", icon: ClipboardCheck, title: "Testing & Certification", to: "/services/testing-certification" },
  { slug: "maintenance", icon: Wrench, title: "Planned Maintenance", to: "/services/maintenance" },
  { slug: "repairs", icon: Cog, title: "Repairs & Upgrades", to: "/services/repairs-upgrades" },
];

export default function ServicesHub() {
  useSEO({
    title: "Fire Safety Services | Fire Safety Systems Ireland",
    description:
      "A complete range of fire safety services — installation, testing, certification, planned maintenance, repairs and upgrades for businesses across Ireland.",
    canonicalPath: "/services",
  });

  return (
    <>
      <PageHero
        eyebrow="Fire Safety Services"
        title="Complete fire safety, delivered end to end."
        subtitle="From design and installation to routine servicing, repairs and multi-site maintenance — find the service you need or speak with our team about your requirements."
        image="https://customer-assets-m6fa6gv7.emergentagent.net/job_fire-cctv-access/artifacts/4694gjo3_image.png"
        imageAlt="Commercial premises supported by Fire Safety Systems"
        ctaPrimary={{ to: "/contact", label: "Request a Free Quote" }}
        ctaSecondary={{ to: "/contact", label: "Speak to Our Team" }}
      />

      {/* Individual services grid */}
      <section className="fss-section bg-white">
        <div className="fss-container">
          <div className="max-w-2xl mb-10">
            <div className="fss-overline">All services</div>
            <h2 className="mt-3 font-display text-3xl md:text-4xl font-semibold text-slate-900 leading-tight">
              Choose the service you need
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
            {CARDS.map((c) => (
              <Link
                key={c.slug}
                to={c.to}
                className="bg-white p-8 hover:bg-slate-900 transition-colors duration-300 group relative"
                data-testid={`hub-card-${c.slug}`}
              >
                <div className="absolute top-0 left-0 right-0 h-1 bg-green-700 group-hover:bg-red-600 transition-colors" />
                <c.icon className="w-9 h-9 text-green-700 group-hover:text-green-500 transition-colors" strokeWidth={1.75} />
                <h3 className="mt-4 font-display text-xl font-semibold text-slate-900 group-hover:text-white transition-colors">
                  {c.title}
                </h3>
                <div className="mt-6 flex items-center gap-2 text-sm font-semibold text-green-700 group-hover:text-green-500">
                  Explore <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Grouped categories */}
      <section className="bg-slate-50 fss-section border-y border-slate-200">
        <div className="fss-container">
          <div className="max-w-2xl mb-10">
            <div className="fss-overline">Service categories</div>
            <h2 className="mt-3 font-display text-3xl md:text-4xl font-semibold text-slate-900 leading-tight">
              What kind of support do you need?
            </h2>
            <p className="mt-3 text-slate-600">
              Use the categories below to find the right area, then jump into
              the detailed service pages.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {GROUPS.map((g, i) => (
              <div key={i} className="fss-card" data-testid={`hub-group-${i}`}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 border border-slate-200 flex items-center justify-center bg-slate-50 rounded-sm">
                    <g.icon className="w-5 h-5 text-green-700" strokeWidth={1.75} />
                  </div>
                  <h3 className="font-display text-xl font-semibold text-slate-900">
                    {g.title}
                  </h3>
                </div>
                <ul className="mt-5 space-y-2 text-sm text-slate-700">
                  {g.items.map((it, j) => (
                    <li key={j} className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-700 mt-2 shrink-0" />
                      {it}
                    </li>
                  ))}
                </ul>
                <div className="mt-6">
                  <Link to={g.to} className="text-sm font-semibold text-green-700 hover:text-green-800 inline-flex items-center gap-1">
                    Learn more
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Not sure */}
      <section className="fss-section bg-white">
        <div className="fss-container">
          <div className="border border-slate-200 p-8 md:p-10 rounded-sm bg-slate-50">
            <div className="max-w-2xl">
              <div className="fss-overline">Not sure which service you need?</div>
              <h2 className="mt-3 font-display text-2xl md:text-3xl font-semibold text-slate-900 leading-tight">
                Tell us about your premises.
              </h2>
              <p className="mt-3 text-slate-600 leading-relaxed">
                Share a few details about your building, current systems or the
                issue you are seeing, and our team will advise you on the
                appropriate next step.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link to="/contact" className="fss-btn fss-btn-primary">
                  Speak to FSS
                </Link>
                <a href="tel:018351454" className="fss-btn fss-btn-outline">
                  Call 01 835 1454
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <CTABanner
        title="Ready to discuss your fire safety systems?"
        subtitle="Contact Fire Safety Systems for a quotation, site survey or planned maintenance proposal."
        primary={{ to: "/contact", label: "Request a Free Quote" }}
        secondary={{ to: "/contact", label: "Arrange a Site Survey" }}
      />
    </>
  );
}
